import { DollarSign, Coins, TrendingUp, TrendingDown } from "lucide-react";

const money = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const toNum = (value) =>
  value === "" || value == null ? 0 : Number(value) || 0;

/**
 * Denomination table with live qty × denom math.
 * mode: "starting" | "closing"
 */
export function DenominationTable({
  items = {},
  startingItems = null,
  onQuantityChange,
  mode = "starting",
  editable = false,
  type = "cash",
  title = null,
}) {
  const denominations = Object.keys(items)
    .map(Number)
    .sort((a, b) => a - b);

  const sectionTotal = denominations.reduce(
    (sum, denom) => sum + denom * toNum(items[denom]),
    0
  );

  const TypeIcon = type === "cash" ? DollarSign : Coins;
  const isCash = type === "cash";
  const accent = isCash
    ? {
        icon: "text-emerald-700 bg-emerald-50 border-emerald-200",
        chip: "bg-emerald-100 text-emerald-800",
        total: "text-emerald-800",
      }
    : {
        icon: "text-amber-700 bg-amber-50 border-amber-200",
        chip: "bg-amber-100 text-amber-800",
        total: "text-amber-800",
      };

  return (
    <div className="space-y-3">
      {title && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md border ${accent.icon}`}
            >
              <TypeIcon className="h-4 w-4" />
            </span>
            <div>
              <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
              <p className="text-[11px] text-gray-400">
                Amount = qty × denomination
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wide text-gray-400">
              Subtotal
            </p>
            <p className={`text-base font-bold ${accent.total}`}>
              ${money(sectionTotal)}
            </p>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2.5 font-semibold">Denom</th>
              <th className="px-3 py-2.5 font-semibold text-right">Qty</th>
              <th className="px-3 py-2.5 font-semibold text-right">Amount</th>
              {mode === "closing" && (
                <th className="px-3 py-2.5 font-semibold text-right">
                  Change
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {denominations.map((denom) => {
              const qty = items[denom];
              const numericQty = toNum(qty);
              const amount = denom * numericQty;
              const startQty =
                startingItems != null ? toNum(startingItems[denom]) : null;
              const startAmount =
                startQty != null ? denom * startQty : null;
              const qtyDiff =
                startQty != null ? numericQty - startQty : null;
              const amountDiff =
                startAmount != null ? amount - startAmount : null;

              return (
                <tr key={denom} className="transition hover:bg-yellow-50/40">
                  <td className="px-3 py-2.5">
                    <span
                      className={`inline-flex rounded px-2 py-0.5 text-xs font-bold ${accent.chip}`}
                    >
                      ${denom.toLocaleString("en-US")}
                    </span>
                    <span className="ml-1.5 text-[10px] uppercase text-gray-400">
                      {isCash ? "bill" : "chip"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {editable ? (
                      <input
                        type="number"
                        min="0"
                        value={qty === "" || qty == null ? "" : qty}
                        onChange={(e) =>
                          onQuantityChange(denom, e.target.value)
                        }
                        className="ml-auto w-20 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-right font-medium text-gray-800 shadow-sm focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-200"
                        placeholder="0"
                      />
                    ) : (
                      <span className="inline-flex min-w-[2.5rem] justify-end rounded bg-gray-100 px-2 py-1 font-semibold text-gray-700">
                        {numericQty}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="font-bold text-gray-900">
                      ${money(amount)}
                    </div>
                    <div className="text-[10px] text-gray-400">
                      {numericQty || 0} × ${denom.toLocaleString("en-US")}
                    </div>
                  </td>
                  {mode === "closing" && (
                    <td className="px-3 py-2.5 text-right">
                      {qtyDiff === 0 || qtyDiff == null ? (
                        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-400">
                          —
                        </span>
                      ) : (
                        <span
                          className={`inline-flex items-center gap-0.5 rounded px-2 py-0.5 text-xs font-semibold ${
                            qtyDiff > 0
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {qtyDiff > 0 ? (
                            <TrendingUp className="h-3 w-3" />
                          ) : (
                            <TrendingDown className="h-3 w-3" />
                          )}
                          {qtyDiff > 0 ? "+" : ""}
                          {qtyDiff}
                          <span className="opacity-80">
                            (${amountDiff > 0 ? "+" : ""}
                            {money(amountDiff)})
                          </span>
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot className="border-t border-gray-200 bg-gray-50">
            <tr>
              <td
                colSpan={2}
                className="px-3 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500"
              >
                {title || (isCash ? "Cash" : "Chips")} total
              </td>
              <td
                className={`px-3 py-3 text-right text-base font-bold ${accent.total}`}
              >
                ${money(sectionTotal)}
              </td>
              {mode === "closing" && <td />}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
